import React, { useState } from "react";
import { Kanban, Activity, Contact, Files, ChartPie } from "lucide-react";
import "../../../styles/kanbans/kanban-tabs-block.css";
import BoardHolder from "./BoardHolder";
import DocumentsTab from "./DocumentsTab";
import ContactsTab from "./ContactsTab";
import Activities from "./activities/Activities";
import MetricsTab from "./MetricsTab";

type TabKey = "board" | "activities" | "contacts" | "documents" | "metrics";

interface KanbanTabsProps {
  onTabChange?: (tab: TabKey) => void;
  onOpenJobModal?: (defaultList?: string) => void;
  shouldOpenNewDocument?: boolean;
  onNewDocumentOpened?: () => void;
  shouldOpenNewContact?: boolean;
  onNewContactOpened?: () => void;
  shouldOpenNewActivity?: boolean;
  onNewActivityOpened?: () => void;
  initialJobId?: string;
  boardId?: string;
  refreshKey?: number;
  defaultJobModalTab?: 'edit' | 'ai-tools' | 'activities' | 'notes' | 'contacts' | 'documents';
}

export default function TabsHeader({ 
  onTabChange, 
  onOpenJobModal, 
  shouldOpenNewDocument, 
  onNewDocumentOpened,
  shouldOpenNewContact,
  onNewContactOpened,
  shouldOpenNewActivity,
  onNewActivityOpened,
  initialJobId,
  boardId,
  refreshKey,
  defaultJobModalTab
}: KanbanTabsProps) {
  const [active, setActive] = useState<TabKey>("board");

  const handleTabChange = (tab: TabKey) => {
    setActive(tab);
    onTabChange?.(tab);
  };

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: "board", label: "Board", icon: <Kanban size={16} /> },
    { key: "activities", label: "Activities", icon: <Activity size={16} /> },
    { key: "contacts", label: "Contacts", icon: <Contact size={16} /> },
    { key: "documents", label: "Documents", icon: <Files size={16} /> },
    { key: "metrics", label: "Metrics", icon: <ChartPie size={16} /> },
  ];

  return (
    <div className="tabs-container">
      <div className="tabs-header">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab-button ${active === tab.key ? "active" : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {active === "board" && (
        <BoardHolder 
          key={refreshKey}
          headerOffset={180} 
          onOpenJobModal={onOpenJobModal}
          initialJobId={initialJobId}
          boardId={boardId}
          defaultJobModalTab={defaultJobModalTab}
        />
      )}
      {active === "activities" && (
        <Activities
          shouldOpenNewActivity={shouldOpenNewActivity}
          onNewActivityOpened={onNewActivityOpened}
          boardId={boardId}
        />
      )}
      {active === "contacts" && (
        <ContactsTab
          shouldOpenNewContact={shouldOpenNewContact}
          onNewContactOpened={onNewContactOpened}
          boardId={boardId}
        />
      )}
      {active === "documents" && (
        <DocumentsTab 
          shouldOpenNewDocument={shouldOpenNewDocument}
          onNewDocumentOpened={onNewDocumentOpened}
          boardId={boardId}
        />
      )}
      {active === "metrics" && (
        <MetricsTab boardId={boardId} />
      )}
    </div>
  );
}